import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AREAS, PROPERTY_TYPES, BEDROOMS, TENURES, SELL_TIMELINES, SELL_REASONS } from "@/lib/constants";
import { Field, Select, SubmitButton } from "@/components/site/FormBits";
import { AccountGate } from "@/components/site/AccountGate";

const EMPTY = { property_address: "", postcode: "", borough: "", property_type: "", bedrooms: "", tenure: "",
  asking_price: "", timeline: "", reason: "", is_vacant: "", notes: "" };

const SellerForm = () => {
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/seller-submissions", {
        ...f, asking_price: f.asking_price ? Number(f.asking_price) : null,
        is_vacant: f.is_vacant === "" ? null : f.is_vacant === "Yes", reason: f.reason || null,
      });
      toast.success("Thank you. We'll review your property and be in touch.");
      nav("/portal");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E4E1D9] p-6 sm:p-10 space-y-6" data-testid="seller-form">
      <Field label="Property address" required><input data-testid="seller-form-address" required value={f.property_address} onChange={set("property_address")} className="field-input" placeholder="House number and street" /></Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Postcode" required><input data-testid="seller-form-postcode" required value={f.postcode} onChange={set("postcode")} className="field-input" placeholder="e.g. SW4 7AA" /></Field>
        <Field label="Area" required><Select testId="seller-form-borough" required options={AREAS} value={f.borough} onChange={set("borough")} /></Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        <Field label="Property type" required><Select testId="seller-form-property-type" required options={PROPERTY_TYPES} value={f.property_type} onChange={set("property_type")} /></Field>
        <Field label="Bedrooms" required><Select testId="seller-form-bedrooms" required options={BEDROOMS} value={f.bedrooms} onChange={set("bedrooms")} /></Field>
        <Field label="Tenure" required><Select testId="seller-form-tenure" required options={TENURES} value={f.tenure} onChange={set("tenure")} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Price expectation (£)" hint="Optional - we'll discuss this with you"><input data-testid="seller-form-asking-price" type="number" min="0" value={f.asking_price} onChange={set("asking_price")} className="field-input" /></Field>
        <Field label="How quickly do you want to sell?" required><Select testId="seller-form-timeline" required options={SELL_TIMELINES} value={f.timeline} onChange={set("timeline")} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Reason for selling"><Select testId="seller-form-reason" options={SELL_REASONS} value={f.reason} onChange={set("reason")} placeholder="Prefer not to say" /></Field>
        <Field label="Is the property vacant?"><Select testId="seller-form-vacant" options={["Yes", "No"]} value={f.is_vacant} onChange={set("is_vacant")} /></Field>
      </div>
      <Field label="Anything else?"><textarea data-testid="seller-form-notes" rows={3} value={f.notes} onChange={set("notes")} className="field-input h-auto py-3" placeholder="Tenancy, works done, key features…" /></Field>
      <SubmitButton loading={loading} testId="seller-form-submit-btn" className="w-full sm:w-auto">Submit property</SubmitButton>
    </form>
  );
};

export default function SellPage() {
  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20 grid lg:grid-cols-12 gap-12" data-testid="sell-page">
      <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
        <p className="eyebrow">For sellers</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">Sell in the quickest time possible.</h1>
        <p className="mt-6 text-[#57534E] leading-relaxed">
          Tell us about your property. We introduce you only to buyers whose funds and chain status we've already reviewed, so you avoid the usual delays.
        </p>
        <ul className="mt-8 space-y-3 text-sm">
          {["No chain-dependent buyers", "No public viewings or listings without your say", "Your details stay with us"].map((t) => (
            <li key={t} className="flex gap-3"><span className="mt-1.5 h-1.5 w-1.5 bg-[#9F7E44] shrink-0" />{t}</li>
          ))}
        </ul>
      </aside>
      <div className="lg:col-span-8"><AccountGate next="/sell"><SellerForm /></AccountGate></div>
    </div>
  );
}
